import React from 'react';
import { FileText, Clock, BookOpen, Loader2, AlertCircle } from 'lucide-react';
import useTenantState from '@/states/tenant.state';
import { useGetActiveChecklistTemplate } from './checklist/useChecklist';

const STATIC_REFERENSI_MAP: Record<string, string[]> = {
  'Mengunduh Dokumen AOS seluruh Jabatan di menu Download Dokumen AOS': ['1. Download dokumen AOS.png'],
  'Mengunggah dan menyimpan softcopy Dokumen AOS seluruh Jabatan ke Google Drive milik perusahaan': [
    '2. Google Drive Perusahaan.png',
  ],
  'Mencetak Dokumen AOS seluruh Jabatan dalam bentuk hardcopy': ['3. Cetak dokumen AOS.png'],
  'Memasukkan hardcopy Dokumen AOS dalam business file per Jabatan. Disarankan business file yang digunakan memiliki warna sesuai dengan warna Brand Identity perusahaan':
    ['4. Business file dokumen AOS.png'],
  'Menempatkan Dokumen AOS seluruh Jabatan di rak arsip milik Bagian SDM': [
    '5. Memasukkan business file ke lemari arsip.png',
  ],
  'Menerbitkan Surat Keputusan (SK) Tim Implementasi AOS': ['6. SK Penunjukan Penanggungjawab Implementasi AOS.pdf'],
  'Mewajibkan Tim Implementasi AOS melihat video cara menggunakan Dokumen AOS': [
    '7. Video Cara Menggunakan Dokumen AOS.mp4',
  ],
  'Melakukan sosialisasi Implementasi AOS kepada seluruh karyawan oleh Tim Implementasi AOS': [
    '8. Sosialisasi IMplementasi AOS.png',
  ],
  'Melakukan penandatanganan Pakta Integritas oleh seluruh karyawan perusahaan (kickoff program Implementasi AOS)': [
    '9. Pakta Integritas Implementasi AOS.pdf',
  ],
  'Membuat desain Poster Purpose, Vision, Mission, Culture, dan Struktur Organisasi Perusahaan': [
    'Poster Purpose, Vision, Mission, Culture, Structure Organization.pdf',
  ],
  'Mencetak dan membingkai Poster Purpose, Vision, Mission, Culture, dan Struktur Organisasi Perusahaan': [
    'Poster Purpose, Vision, Mission, Culture, Structure Organization.pdf',
  ],
  'Memasang Poster Purpose, Vision, Mission, Culture, dan Struktur Organisasi Perusahaan di tempat strategis perusahaan':
    ['Poster Purpose, Vision, Mission, Culture, Structure Organization.pdf'],
  'Membaca Purpose, Vision, Mission, dan Culture Perusahaan bersama seluruh tim pada saat briefing pagi': [
    '13. Membaca Purpose, Vision, Mission, dan Culture Perusahaan bersama seluruh tim pada saat briefing pagi.png',
  ],
  'Menyusun jadwal pelatihan Dokumen AOS setiap Jabatan terkait': ['14. Jadwal Training SOP.pdf'],
  'Mencetak salinan Dokumen AOS setiap Jabatan (fotocopy) untuk proses pelatihan': ['15. Fotokopian dokumen AOS.png'],
  'Memberikan salinan Dokumen AOS kepada setiap Jabatan': ['16. penyerahan fotokopian dokumen AOS.png'],
  'Melaksanakan pelatihan Dokumen AOS yang dilakukan oleh atasan setiap Jabatan': ['17. Pelatihan Dokumen AOS.png'],
  'Membuat desain Business Goal (AOS #4)': ['18. Poster target omset.png'],
  'Mencetak dan membingkai poster Business Goals': ['19. Mencetak Membingkai Memasang Business Goals.png'],
  'Memasang poster Business Goal di Ruang Kerja Pimpinan Perusahaan': [
    '19. Mencetak Membingkai Memasang Business Goals.png',
  ],
  'Menyusun Tata Tertib Perusahaan and melakukan sosialisasi': ['Contoh Tata Tertib Perusahaan.pdf'],
  'Menyusun Tata Tertib Perusahaan dan melakukan sosialisasi': ['Contoh Tata Tertib Perusahaan.pdf'],
  'Menyusun Tabel Ketentuan Kehadiran, Izin, dan Cuti and melakukan sosialisasi': [
    'Contoh Tabel Ketentuan Kehadiran.pdf',
  ],
  'Menyusun Tabel Ketentuan Kehadiran, Izin, dan Cuti dan melakukan sosialisasi': [
    'Contoh Tabel Ketentuan Kehadiran.pdf',
  ],
  'Menyusun dan menandatangani Addendum Kontrak Kerja setiap karyawan': ['Kontrak Kerja Staf Masak Bakso Fedas.pdf'],
  'Menerapkan atribut identitas karyawan sesuai dengan standar perusahaan (Lanyard, ID Card, Seragam)': [
    'ID Card Karyawan Bakso Fedas.png',
    'Poster Grooming Karyawan Bakso Fedas.png',
  ],
};

const getHref = (ref: string) => {
  const trimmed = ref.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  if (trimmed.startsWith('www.')) {
    return `https://${trimmed}`;
  }
  if (trimmed.includes('.') && !trimmed.includes(' ')) {
    return `https://${trimmed}`;
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const parseReference = (ref: string): { label: string; href: string } => {
  const trimmed = ref.trim();
  if (trimmed.includes('|')) {
    const parts = trimmed.split('|');
    const label = parts[0].trim();
    const rawLink = parts.slice(1).join('|').trim();
    return {
      label: label || rawLink,
      href: getHref(rawLink || label),
    };
  }
  return {
    label: trimmed,
    href: getHref(trimmed),
  };
};

export default function PanduanImplementasi() {
  const { currentTenant } = useTenantState();
  const { data: activeTemplate, isLoading, isError } = useGetActiveChecklistTemplate(currentTenant?.id);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-[#0D0D0D]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-[#071753] dark:text-blue-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Memuat panduan implementasi...</p>
        </div>
      </div>
    );
  }

  if (isError || !activeTemplate) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-gray-50 dark:bg-[#0D0D0D] px-4">
        <div className="text-center max-w-md bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Panduan tidak ditemukan</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Aktifkan template checklist terlebih dahulu untuk memuat panduan implementasi.
          </p>
        </div>
      </div>
    );
  }

  const sortedSections = [...(activeTemplate.sections || [])].sort((a, b) => a.order_index - b.order_index);
  let globalItemIndex = 1;

  return (
    <section className="w-full min-h-screen bg-gray-50 dark:bg-[#0D0D0D]">
      {/* Page header */}
      <div className="bg-white dark:bg-gray-900 px-6 py-5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: '#071753' }}
          >
            <BookOpen size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Panduan Implementasi AOS</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Panduan Implementasi AOS berdasarkan Template Aktif
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="p-5 overflow-x-auto">
        <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
          <table className="w-full text-sm border-collapse">
            {/* Table head */}
            <thead>
              <tr style={{ backgroundColor: '#071753' }} className="text-white">
                <th className="px-4 py-3 text-left font-semibold w-12 border-r border-white/10">No</th>
                <th className="px-4 py-3 text-left font-semibold border-r border-white/10">PANDUAN IMPLEMENTASI AOS</th>
                <th className="px-4 py-3 text-left font-semibold w-32 border-r border-white/10 whitespace-nowrap">
                  <span className="flex items-center gap-1.5">
                    <Clock size={13} />
                    Target Waktu
                  </span>
                </th>
                <th className="px-4 py-3 text-left font-semibold w-64">
                  <span className="flex items-center gap-1.5">
                    <FileText size={13} />
                    Referensi
                  </span>
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedSections.map(section => {
                const sortedSubSections = [...(section.sub_sections || [])].sort(
                  (a, b) => a.order_index - b.order_index,
                );

                return (
                  <React.Fragment key={`section-${section.id}`}>
                    {/* Section title row */}
                    <tr className="bg-[#dce8f8] dark:bg-[#0a1628]">
                      <td
                        colSpan={4}
                        className="px-4 py-2 font-semibold text-xs tracking-wide border-b border-gray-200 dark:border-gray-700 text-[#071753] dark:text-blue-200"
                      >
                        {section.name}
                      </td>
                    </tr>

                    {/* Subsections & Items */}
                    {sortedSubSections.map(subSection => {
                      const sortedItems = [...(subSection.items || [])].sort((a, b) => a.order_index - b.order_index);

                      return (
                        <React.Fragment key={`sub-section-${subSection.id}`}>
                          {/* Subsection header row if it has a non-empty name */}
                          {subSection.name && subSection.name !== section.name && (
                            <tr className="bg-blue-50 dark:bg-[#0e1e3a]">
                              <td
                                colSpan={4}
                                className="px-4 py-2 font-semibold text-xs tracking-wide border-b border-gray-200 dark:border-gray-700 text-blue-800 dark:text-blue-300 pl-8"
                              >
                                {subSection.name}
                              </td>
                            </tr>
                          )}

                          {/* Items rows */}
                          {sortedItems.map((item, rowIdx) => {
                            const descriptionTrimmed = item.description?.trim() || '';
                            const references =
                              item.references && item.references.length > 0
                                ? item.references
                                : STATIC_REFERENSI_MAP[descriptionTrimmed] || [];

                            return (
                              <tr
                                key={`item-${item.id}`}
                                className={`border-b border-gray-100 dark:border-gray-800 transition-colors hover:bg-blue-50/40 dark:hover:bg-gray-800/60 ${
                                  rowIdx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/70 dark:bg-gray-800/30'
                                }`}
                              >
                                {/* No */}
                                <td className="px-4 py-3 text-center align-top font-semibold text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-gray-800">
                                  {globalItemIndex++}
                                </td>

                                {/* Kegiatan */}
                                <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-200 leading-relaxed border-r border-gray-100 dark:border-gray-800">
                                  {item.description}
                                </td>

                                {/* Target Waktu */}
                                <td className="px-4 py-3 align-top whitespace-nowrap text-gray-600 dark:text-gray-300 border-r border-gray-100 dark:border-gray-800">
                                  {item.estimated_work_days ? `Hari ${item.estimated_work_days}` : '—'}
                                </td>

                                {/* Referensi */}
                                <td className="px-4 py-3 align-top">
                                  {references.length > 0 ? (
                                    <ul className="space-y-1.5">
                                      {references.map(ref => {
                                        const { label, href } = parseReference(ref);
                                        return (
                                          <li
                                            key={ref}
                                            className="flex items-start gap-1.5 text-gray-500 dark:text-gray-400"
                                          >
                                            <FileText
                                              size={12}
                                              className="mt-0.5 shrink-0 text-gray-400 dark:text-gray-500"
                                            />
                                            <a
                                              href={href}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline leading-snug break-all font-medium"
                                            >
                                              {label}
                                            </a>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : (
                                    <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
